import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent'
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { supabase } from '../../shared/supabase/supabaseClient';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type {
  CustomerOrderRow,
  CustomerOrderDetail,
  CustomerOrderDiscountRow,
  CustomerOrderItemRow,
  OrderStatus,
} from '../domain/orderTypes';
import { historyStatusesForKind, OPEN_ORDER_STATUSES, type OrderHistoryKind } from '../domain/orderQueue';
import type { OrderActivityEvent } from '@shared/orderActivity';
import { accentQueryVariants } from '../../shared/format/searchText';

const ORDER_LIST_SELECT =
  'id, status, pickup_type, delivery_fulfillment, total_amount, notes, created_at, updated_at, confirmed_at, ready_at, picked_up_at, customer_id, branch_id, organization_id, contains_restricted, payment_provider, payment_status, pickup_time_slot, order_revision, resolution_status, customer_action_deadline, rider_tip_amount, delivery_address, delivery_lat, delivery_lng, delivery_fee, delivery_eta_minutes, discount_total, cancel_reason, cancelled_at, rematch_attempts, sla_extended_until, sale_id, estimated_ready_at, display_code, pickup_verification_required, delivery_pin_required, customer_display_name, customer_display_phone, customer_profiles(full_name, phone)';

const OPEN_ORDERS_LIMIT = 80;
const HISTORY_PAGE_SIZE = 30;

export type FetchOrderHistoryParams = {
  fromIso: string;
  toIso: string;
  kind: OrderHistoryKind;
  limit?: number;
  offset?: number;
};

export type FetchOrderHistoryResult = {
  rows: CustomerOrderRow[];
  hasMore: boolean;
};

type OrderRowDB = {
  id: string;
  status: OrderStatus;
  pickup_type: 'pickup' | 'delivery';
  delivery_fulfillment: 'merchant' | 'platform_rider' | null;
  total_amount: number;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  confirmed_at: string | null;
  ready_at: string | null;
  picked_up_at: string | null;
  customer_id: string;
  branch_id: string;
  organization_id: string;
  contains_restricted: boolean;
  payment_provider: string | null;
  payment_status: string | null;
  pickup_time_slot: string | null;
  order_revision: number;
  resolution_status: 'none' | 'pending_customer' | 'approved' | 'rejected' | 'expired';
  customer_action_deadline: string | null;
  rider_tip_amount?: number | null;
  delivery_address?: string | null;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  delivery_fee?: number | null;
  delivery_eta_minutes?: number | null;
  discount_total?: number | null;
  cancel_reason?: string | null;
  cancelled_at?: string | null;
  rematch_attempts?: number | null;
  sla_extended_until?: string | null;
  sale_id?: string | null;
  estimated_ready_at?: string | null;
  display_code?: string | null;
  pickup_verification_required?: boolean | null;
  delivery_pin_required?: boolean | null;
  customer_display_name?: string | null;
  customer_display_phone?: string | null;
  customer_profiles: { full_name: string; phone: string | null } | { full_name: string; phone: string | null }[] | null;
};

type OrderChangeRow = {
  id: string;
  order_id: string;
  order_item_id: string;
  change_type: 'remove' | 'replace' | 'reduce_quantity';
  proposed_product_id: string | null;
  proposed_product_name: string | null;
  proposed_unit_price: number | null;
  proposed_quantity: number;
  original_product_name: string;
  original_unit_price: number;
  original_quantity: number;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  expires_at: string;
};

export type ReplacementProduct = {
  id: string;
  name: string;
  price: number;
  stock: number;
};

function mapRow(row: OrderRowDB): CustomerOrderRow {
  const customerProfile = Array.isArray(row.customer_profiles)
    ? row.customer_profiles[0]
    : row.customer_profiles;

  return {
    id: row.id,
    status: row.status,
    pickup_type: row.pickup_type,
    delivery_fulfillment: row.delivery_fulfillment ?? null,
    total_amount: Number(row.total_amount),
    notes: row.notes,
    created_at: row.created_at,
    updated_at: row.updated_at,
    confirmed_at: row.confirmed_at,
    ready_at: row.ready_at,
    picked_up_at: row.picked_up_at,
    customer_id: row.customer_id,
    customer_name: row.customer_display_name?.trim() || customerProfile?.full_name || 'Cliente',
    customer_phone: row.customer_display_phone?.trim() || customerProfile?.phone || null,
    branch_id: row.branch_id,
    organization_id: row.organization_id,
    contains_restricted: row.contains_restricted ?? false,
    payment_provider: row.payment_provider ?? null,
    payment_status: row.payment_status ?? null,
    pickup_time_slot: row.pickup_time_slot ?? null,
    order_revision: row.order_revision ?? 1,
    resolution_status: row.resolution_status ?? 'none',
    customer_action_deadline: row.customer_action_deadline ?? null,
    rider_tip_amount: row.rider_tip_amount != null ? Number(row.rider_tip_amount) : 0,
    delivery_address: row.delivery_address ?? null,
    delivery_lat: row.delivery_lat != null ? Number(row.delivery_lat) : null,
    delivery_lng: row.delivery_lng != null ? Number(row.delivery_lng) : null,
    delivery_fee: row.delivery_fee != null ? Number(row.delivery_fee) : null,
    delivery_eta_minutes: row.delivery_eta_minutes != null ? Number(row.delivery_eta_minutes) : null,
    discount_total: row.discount_total != null ? Number(row.discount_total) : 0,
    cancel_reason: row.cancel_reason ?? null,
    cancelled_at: row.cancelled_at ?? null,
    rematch_attempts: row.rematch_attempts != null ? Number(row.rematch_attempts) : 0,
    sla_extended_until: row.sla_extended_until ?? null,
    sale_id: row.sale_id ?? null,
    estimated_ready_at: row.estimated_ready_at ?? null,
    // ponytail: UUID[:8] only if pre-migration row somehow lacks code
    display_code: (row.display_code ?? row.id.slice(0, 8)).toUpperCase(),
    pickup_verification_required: row.pickup_verification_required === true,
    delivery_pin_required: row.delivery_pin_required === true,
  };
}

function isVisibleToStore(row: OrderRowDB): boolean {
  // MP unpaid must never appear in kitchen lists
  if (row.payment_provider === 'mercadopago' && row.payment_status !== 'paid') return false;
  return true;
}

/**
 * Cache corto de la cola viva: la pantalla de pedidos refetchea en cada focus y
 * el owner-panel pide el count por sucursal; bursts de focus/realtime/branch
 * comparten un solo request. `force` saltea el TTL (refresh manual, realtime).
 */
const OPEN_ORDERS_CACHE_TTL_MS = 10_000;
const openOrdersCache = createKeyedTtlCache(OPEN_ORDERS_CACHE_TTL_MS);
const openOrdersCountCache = createKeyedTtlCache(OPEN_ORDERS_CACHE_TTL_MS);

function openOrdersCacheKey(orgId: string, branchId: string | null | undefined, statuses: OrderStatus[]): string {
  return `${orgId}|${branchId?.trim() ?? ''}|${statuses.join(',')}`;
}

/** Solo tests: limpia ambos caches de la cola viva. */
export function resetOpenOrdersCachesForTests(): void {
  openOrdersCache.clear();
  openOrdersCountCache.clear();
}

/** Cola viva: solo estados abiertos de la sucursal (o org si no hay branch). */
export async function fetchOpenOrders(
  orgId: string,
  branchId?: string | null,
  statuses: OrderStatus[] = OPEN_ORDER_STATUSES,
  opts?: { force?: boolean },
): Promise<CustomerOrderRow[]> {
  return openOrdersCache.get(
    openOrdersCacheKey(orgId, branchId, statuses),
    async () => {
      let query = supabase
        .from('customer_orders')
        .select(ORDER_LIST_SELECT)
        .eq('organization_id', orgId)
        .in('status', statuses)
        // Excluir unpaid MP en el query para no gastar el LIMIT en pedidos invisibles
        .or('payment_provider.is.null,payment_provider.neq.mercadopago,payment_status.eq.paid')
        .order('created_at', { ascending: false })
        .limit(OPEN_ORDERS_LIMIT);

      const branch = branchId?.trim();
      if (branch) {
        query = query.eq('branch_id', branch);
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data as OrderRowDB[]).filter(isVisibleToStore).map(mapRow);
    },
    { force: opts?.force },
  );
}

/**
 * Count-only de la cola viva (HEAD, sin filas): para consumers que solo
 * necesitan el número (owner-panel por sucursal). Misma visibilidad que
 * fetchOpenOrders, sin pagar el SELECT ancho + join.
 */
export async function countOpenOrders(
  orgId: string,
  branchId?: string | null,
  statuses: OrderStatus[] = OPEN_ORDER_STATUSES,
  opts?: { force?: boolean },
): Promise<number> {
  return openOrdersCountCache.get(
    openOrdersCacheKey(orgId, branchId, statuses),
    async () => {
      let query = supabase
        .from('customer_orders')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .in('status', statuses)
        .or('payment_provider.is.null,payment_provider.neq.mercadopago,payment_status.eq.paid');

      const branch = branchId?.trim();
      if (branch) {
        query = query.eq('branch_id', branch);
      }

      const { count, error } = await query;
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
    { force: opts?.force },
  );
}

/** Historial paginado: completados / cancelados en rango de fechas (por created_at). */
export async function fetchOrderHistory(
  orgId: string,
  branchId: string,
  params: FetchOrderHistoryParams,
): Promise<FetchOrderHistoryResult> {
  const branch = branchId.trim();
  if (!branch) {
    return { rows: [], hasMore: false };
  }

  const limit = Math.max(1, Math.min(params.limit ?? HISTORY_PAGE_SIZE, 100));
  const offset = Math.max(0, params.offset ?? 0);
  const statuses = historyStatusesForKind(params.kind);

  const { data, error } = await supabase
    .from('customer_orders')
    .select(ORDER_LIST_SELECT)
    .eq('organization_id', orgId)
    .eq('branch_id', branch)
    .in('status', statuses)
    // Misma regla que isVisibleToStore, en SQL: el range/limit pagina sobre filas
    // visibles (filtrar post-fetch dejaba páginas cortas y drift de offset).
    .or('payment_provider.is.null,payment_provider.neq.mercadopago,payment_status.eq.paid')
    .gte('created_at', params.fromIso)
    .lte('created_at', params.toIso)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit);

  if (error) throw new Error(error.message);
  const mapped = (data as OrderRowDB[]).filter(isVisibleToStore).map(mapRow);
  const hasMore = mapped.length > limit;
  return { rows: hasMore ? mapped.slice(0, limit) : mapped, hasMore };
}

export async function fetchOrderById(orderId: string): Promise<CustomerOrderDetail | null> {
  const { data: order, error } = await supabase
    .from('customer_orders')
    .select(ORDER_LIST_SELECT)
    .eq('id', orderId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!order) return null;
  if (!isVisibleToStore(order as OrderRowDB)) return null;

  const { data: items, error: itemsError } = await supabase
    .from('customer_order_items')
    .select(
      'id, product_id, product_name, unit_price, unit_price_original, discount_amount, quantity, line_total, item_status, effective_product_name, effective_unit_price, effective_line_total, unit_of_measure, options, line_note',
    )
    .eq('order_id', orderId)
    .order('product_name', { ascending: true });

  if (itemsError) throw new Error(itemsError.message);

  const { data: customerProfile, error: customerProfileError } = await supabase
    .from('customer_profiles')
    .select('full_name, phone')
    .eq('id', (order as OrderRowDB).customer_id)
    .maybeSingle();

  if (customerProfileError) throw new Error(customerProfileError.message);

  const { data: discounts, error: discountsError } = await supabase
    .from('customer_order_discounts')
    .select('id, promotion_id, label, discount_amount, scope, line_id')
    .eq('order_id', orderId)
    .order('created_at', { ascending: true });

  if (discountsError) throw new Error(discountsError.message);

  const { data: changes, error: changesError } = await supabase
    .from('customer_order_item_changes')
    .select('id, order_id, order_item_id, change_type, proposed_product_id, proposed_product_name, proposed_unit_price, proposed_quantity, original_product_name, original_unit_price, original_quantity, reason, status, expires_at')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false });

  if (changesError) throw new Error(changesError.message);

  const { data: events, error: eventsError } = await supabase
    .from('customer_order_events')
    .select('id, order_id, event_type, payload, actor_role, actor_id, created_at')
    .eq('order_id', orderId)
    .order('created_at', { ascending: true });

  if (eventsError) throw new Error(eventsError.message);

  const orderRow = order as OrderRowDB;
  const normalizedOrder: OrderRowDB = {
    ...orderRow,
    customer_profiles: customerProfile ?? orderRow.customer_profiles,
  };

  return {
    ...mapRow(normalizedOrder),
    items: ((items ?? []) as CustomerOrderItemRow[]).map((item) => ({
      ...item,
      item_status: item.item_status ?? 'active',
      effective_product_name: item.effective_product_name ?? null,
      effective_unit_price: item.effective_unit_price != null ? Number(item.effective_unit_price) : null,
      effective_line_total: item.effective_line_total != null ? Number(item.effective_line_total) : null,
      unit_price: Number(item.unit_price),
      quantity: Number(item.quantity),
      line_total: Number(item.line_total),
      unit_price_original:
        item.unit_price_original != null ? Number(item.unit_price_original) : null,
      discount_amount: item.discount_amount != null ? Number(item.discount_amount) : 0,
      options: Array.isArray(item.options) ? item.options : [],
      line_note: item.line_note?.trim() ? item.line_note.trim() : null,
    })),
    discounts: ((discounts ?? []) as CustomerOrderDiscountRow[]).map((d) => ({
      id: d.id,
      promotion_id: d.promotion_id ?? null,
      label: (d.label ?? '').trim() || 'Descuento',
      discount_amount: Number(d.discount_amount ?? 0),
      scope: d.scope ?? 'line',
      line_id: d.line_id ?? null,
    })),
    changes: (changes ?? []) as OrderChangeRow[],
    events: ((events ?? []) as OrderActivityEvent[]).map((event) => ({
      ...event,
      payload:
        event.payload && typeof event.payload === 'object' && !Array.isArray(event.payload)
          ? (event.payload as Record<string, unknown>)
          : {},
    })),
  };
}

export async function fetchOrderItemStocks(
  branchId: string,
  productIds: string[],
): Promise<Record<string, number>> {
  if (productIds.length === 0) return {};
  const { data, error } = await supabase
    .schema('inventory')
    .from('stocks')
    .select('product_id, quantity')
    .eq('branch_id', branchId)
    .in('product_id', productIds);
  if (error) throw new Error(error.message);
  const map: Record<string, number> = {};
  for (const row of data ?? []) {
    map[row.product_id as string] = Number(row.quantity);
  }
  return map;
}

export type CompleteOrderPhase = 'completed' | 'qr_pending';

export type CompleteOrderResult = {
  saleId?: string;
  phase?: CompleteOrderPhase;
  finalStatus?: string;
};

export type UpdateOrderStatusOptions = {
  /** Required by complete-customer-order when the order is pay_at_pickup cash/transfer. */
  cashSessionId?: string | null;
  paymentMethodId?: 'cash' | 'transfer' | 'mercadopago_qr' | null;
  completeMode?: 'collect' | 'qr_init' | 'qr_finalize';
};

export class CashSessionRequiredError extends Error {
  readonly code = 'cash_session_required' as const;
  constructor(message = 'Abrí caja para cobrar este pedido en efectivo.') {
    super(message);
    this.name = 'CashSessionRequiredError';
  }
}

function throwUnexpectedOrderStatusFailure(
  orderId: string,
  status: OrderStatus,
  error: Error,
): never {
  captureOpsFailure({
    module: 'orders',
    op: 'orders.status',
    error,
    ingestError: error.message,
    itemId: orderId,
    extra: { orderId, status },
  });
  throw error;
}

export async function updateOrderStatus(
  orderId: string,
  status: OrderStatus,
  options?: UpdateOrderStatusOptions,
): Promise<CompleteOrderResult | void> {
  // confirmed and picked_up require Edge Functions for stock/POS operations
  addSentryBreadcrumb('order_status', `Pedido ${orderId} → ${status}`, 'info', { orderId, status });
  if (status === 'confirmed') {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No session');
    const res = await fetch(
      `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/confirm-customer-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      },
    );
    const data = (await res.json()) as { ok?: boolean; error?: string };
    if (!res.ok || !data.ok) {
      throwUnexpectedOrderStatusFailure(
        orderId,
        status,
        new Error(data.error ?? 'Error al confirmar pedido'),
      );
    }
    return;
  }

  if (status === 'picked_up') {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No session');
    const body: {
      order_id: string;
      cash_session_id?: string;
      payment_method_id?: string;
      mode?: string;
    } = { order_id: orderId };
    const cashSessionId = options?.cashSessionId?.trim();
    if (cashSessionId) body.cash_session_id = cashSessionId;
    if (options?.paymentMethodId) body.payment_method_id = options.paymentMethodId;
    if (options?.completeMode) body.mode = options.completeMode;
    const res = await fetch(
      `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/complete-customer-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      },
    );
    const data = (await res.json()) as {
      ok?: boolean;
      error?: string;
      message?: string;
      sale_id?: string;
      phase?: CompleteOrderPhase;
      final_status?: string;
    };
    if (res.status === 409 && data.error === 'cash_session_required') {
      throw new CashSessionRequiredError(data.message ?? data.error);
    }
    if (!res.ok || !data.ok) {
      throwUnexpectedOrderStatusFailure(
        orderId,
        status,
        new Error(data.message ?? data.error ?? 'Error al completar pedido'),
      );
    }
    return {
      saleId: data.sale_id,
      phase: data.phase,
      finalStatus: data.final_status,
    };
  }

  if (status === 'dispatched') {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No session');
    const res = await fetch(
      `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/dispatch-customer-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      },
    );
    const data = (await res.json()) as { ok?: boolean; error?: string };
    if (!res.ok || !data.ok) {
      throwUnexpectedOrderStatusFailure(
        orderId,
        status,
        new Error(data.error ?? 'Error al despachar pedido'),
      );
    }
    return;
  }

  // Merchant cancel: RPC releases hard-deducted stock immediately
  if (status === 'cancelled') {
    const { data, error } = await supabase.rpc('cancel_customer_order_with_stock', {
      p_order_id: orderId,
    });
    if (error) {
      const msg = error.message ?? '';
      if (msg.includes('cannot_cancel_status')) {
        throw new Error('Este pedido ya salió. No se puede cancelar.');
      }
      if (msg.includes('order_not_found')) {
        throw new Error('Pedido no encontrado');
      }
      if (
        msg.includes('branch_access_denied') ||
        msg.includes('not_authenticated') ||
        msg.includes('module_permission_denied')
      ) {
        throw new Error('No tenés permiso para cancelar este pedido');
      }
      throwUnexpectedOrderStatusFailure(orderId, status, new Error(msg || 'Error al cancelar pedido'));
    }
    if (!data || (data as { ok?: boolean }).ok === false) {
      throwUnexpectedOrderStatusFailure(orderId, status, new Error('No se pudo cancelar el pedido'));
    }
    return;
  }

  if (status === 'preparing' || status === 'ready' || status === 'delivered') {
    const { data, error } = await supabase.rpc('merchant_set_customer_order_status', {
      p_order_id: orderId,
      p_status: status,
    });
    if (error) {
      const msg = error.message ?? '';
      if (msg.includes('module_permission_denied') || msg.includes('branch_access_denied')) {
        throw new Error('No tenés permiso para actualizar este pedido');
      }
      if (msg.includes('order_not_found')) {
        throw new Error('Pedido no encontrado');
      }
      throwUnexpectedOrderStatusFailure(
        orderId,
        status,
        new Error(msg || 'Error al actualizar el estado'),
      );
    }
    if (!data || (data as { ok?: boolean }).ok === false) {
      throwUnexpectedOrderStatusFailure(orderId, status, new Error('No se pudo actualizar el estado'));
    }
    return;
  }

  throwUnexpectedOrderStatusFailure(orderId, status, new Error(`Estado no soportado: ${status}`));
}

export type ExtendPreparingSlaResult = {
  sla_extended_until: string;
  estimated_ready_at: string;
};

export async function extendPreparingSla(
  orderId: string,
  extraMinutes = 15,
): Promise<ExtendPreparingSlaResult> {
  const { data, error } = await supabase.rpc('extend_preparing_sla', {
    p_order_id: orderId,
    p_extra_minutes: extraMinutes,
  });
  if (error) {
    const msg = error.message ?? '';
    if (msg.includes('module_permission_denied') || msg.includes('branch_access_denied')) {
      throw new Error('No tenés permiso para actualizar este pedido');
    }
    if (msg.includes('order_not_found')) {
      throw new Error('Pedido no encontrado');
    }
    if (msg.includes('not_preparing')) {
      throw new Error('Solo se puede extender el tiempo en preparación');
    }
    throw new Error(msg || 'Error al extender el tiempo');
  }
  const result = data as { ok?: boolean; sla_extended_until?: string; estimated_ready_at?: string } | null;
  if (!result || result.ok === false) {
    throw new Error('No se pudo extender el tiempo');
  }
  return {
    sla_extended_until: result.sla_extended_until ?? new Date().toISOString(),
    estimated_ready_at: result.estimated_ready_at ?? new Date().toISOString(),
  };
}

export async function fetchReplacementProducts(branchId: string, organizationId: string, excludeProductId: string, search?: string): Promise<ReplacementProduct[]> {
  let query = supabase
    .from('products')
    .select('id, name, price')
    .eq('organization_id', organizationId)
    .eq('is_public', true)
    .neq('id', excludeProductId)
    .order('name');
  if (search && search.trim()) {
    const variants = accentQueryVariants(search);
    query = query.or(variants.map((v) => `name.ilike.%${v}%`).join(','));
  }
  const { data: products, error } = await query.limit(30);
  if (error) throw new Error(error.message);
  const ids = (products ?? []).map((product) => product.id);
  if (ids.length === 0) return [];
  const { data: stocks, error: stockError } = await supabase
    .schema('inventory')
    .from('stocks')
    .select('product_id, quantity')
    .eq('branch_id', branchId)
    .in('product_id', ids);
  if (stockError) throw new Error(stockError.message);
  const stockByProduct = new Map((stocks ?? []).map((stock) => [stock.product_id, Number(stock.quantity)]));
  // OBS #4 fix: filter out products with zero stock in this branch — they
  // can't be used as replacements and showing them only frustrates the user.
  return (products ?? [])
    .map((product) => ({ id: product.id, name: product.name, price: Number(product.price), stock: stockByProduct.get(product.id) ?? 0 }))
    .filter((p) => p.stock > 0);
}

export async function proposeOrderChange(params: {
  orderId: string;
  orderItemId: string;
  changeType: 'remove' | 'replace' | 'reduce_quantity';
  proposedProductId?: string;
  proposedQuantity?: number;
  reason?: string;
}): Promise<void> {
  const { data, error, presented, kind } = await invokeEdgeOrPresent('propose-order-change', {
    body: {
      order_id: params.orderId,
      order_item_id: params.orderItemId,
      change_type: params.changeType,
      proposed_product_id: params.proposedProductId,
      proposed_quantity: params.proposedQuantity,
      reason: params.reason,
      idempotency_key: crypto.randomUUID(),
    },
  }, {
    module: 'orders',
    op: 'orders.propose_change',
    fallbackMessage: 'No se pudo proponer el cambio',
  });
  const body = data as { error?: string } | null;
  if (error || body?.error) {
    const fail = new Error(body?.error ?? error?.message ?? 'No se pudo proponer el cambio');
    const msg = fail.message.toLowerCase();
    if (msg.includes('module_permission_denied') || msg.includes('branch_access_denied') || kind === 'permission') {
      throw fail;
    }
    if (!presented) {
      captureOpsFailure({
        module: 'orders',
        op: 'orders.propose_change',
        error: fail,
        ingestError: fail.message,
        itemId: params.orderId,
        extra: { orderId: params.orderId, orderItemId: params.orderItemId, changeType: params.changeType },
      });
    }
    throw fail;
  }
}

export async function refundOrder(orderId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No session');
  const res = await fetch(
    `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/process-refund`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ order_id: orderId }),
    },
  );
  const data = (await res.json()) as { ok?: boolean; error?: string };
  if (!res.ok || !data.ok) {
    const fail = new Error(data.error ?? 'Error al procesar el reembolso');
    const msg = fail.message.toLowerCase();
    if (msg.includes('module_permission_denied') || msg.includes('branch_access_denied')) {
      throw fail;
    }
    captureOpsFailure({
      module: 'orders',
      op: 'orders.refund',
      error: fail,
      ingestError: fail.message,
      itemId: orderId,
      extra: { orderId },
    });
    throw fail;
  }
}
